import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Textarea from '../../../components/ui/textarea';
import { toast } from 'sonner';
import s3UploadService from '../../../services/s3UploadService';

const MAX_ATTACHMENT_SIZE = 15 * 1024 * 1024;
const ALLOWED_ATTACHMENT_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];

const severityOptions = [
  { value: 'low', label: 'Faible' },
  { value: 'medium', label: 'Moyen' },
  { value: 'high', label: 'Eleve' },
  { value: 'critical', label: 'Critique' },
];

const categoryOptions = [
  { value: 'quality', label: 'Probleme de qualite' },
  { value: 'delivery', label: 'Probleme de livraison' },
  { value: 'communication', label: 'Probleme de communication' },
  { value: 'technical', label: 'Probleme technique' },
  { value: 'other', label: 'Autre' },
];

const ReportCaseIncidentModal = ({ isOpen, onClose, currentUserId, onIncidentCreated }) => {
  const [cases, setCases] = useState([]);
  const [loadingCases, setLoadingCases] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    severity: 'medium',
    category: 'quality',
  });
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !currentUserId) return;
    const fetchCases = async () => {
      setLoadingCases(true);
      const { data, error } = await supabase
        .from('cases')
        .select('id, case_number, patient_id, patient:patients(name)')
        .eq('user_profile_id', currentUserId)
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) {
        console.error('Error fetching cases for incident modal:', error);
        toast.error('Impossible de charger les conceptions');
      } else {
        setCases(
          (data || []).map((row) => ({
            id: row.id,
            caseNumber: row.case_number || '—',
            patientName: row.patient?.name || '—',
            patientId: row.patient_id,
          }))
        );
      }
      setLoadingCases(false);
    };
    fetchCases();
  }, [isOpen, currentUserId]);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setSelectedCaseId(null);
      setFormData({ title: '', description: '', severity: 'medium', category: 'quality' });
      setAttachment(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const filteredCases = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return cases;
    return cases.filter(
      (c) =>
        String(c.caseNumber).toLowerCase().includes(term) ||
        String(c.patientName).toLowerCase().includes(term)
    );
  }, [cases, search]);

  const selectedCase = useMemo(
    () => cases.find((c) => c.id === selectedCaseId) || null,
    [cases, selectedCaseId]
  );

  if (!isOpen) return null;

  const validateAttachment = (file) => {
    if (!file) return null;
    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      return 'Format invalide. Fichiers acceptes: PDF, PNG, JPEG.';
    }
    if (file.size > MAX_ATTACHMENT_SIZE) {
      return 'Fichier trop volumineux. Taille maximale: 15 Mo.';
    }
    return null;
  };

  const handleAttachmentChange = (event) => {
    const file = event.target?.files?.[0] || null;
    const validationMessage = validateAttachment(file);
    if (validationMessage) {
      toast.error(validationMessage);
      event.target.value = '';
      return;
    }
    setAttachment(file);
  };

  const handleSubmit = async () => {
    if (!currentUserId || !selectedCaseId || !formData.title || !formData.description) return;
    const attachmentError = validateAttachment(attachment);
    if (attachmentError) {
      toast.error(attachmentError);
      return;
    }

    setSubmitting(true);
    try {
      const { count: openCount, error: countError } = await supabase
        .from('case_incidents')
        .select('id', { count: 'exact', head: true })
        .eq('case_id', selectedCaseId)
        .neq('status', 'resolved');

      if (countError) throw countError;
      if ((openCount ?? 0) > 0) {
        toast.error(
          'Un incident est déjà en cours sur cette conception. Attendez sa clôture avant d’en créer un nouveau.',
        );
        return;
      }

      const { data: incidentRow, error: incidentError } = await supabase
        .from('case_incidents')
        .insert([
          {
            case_id: selectedCaseId,
            incident_type: formData.category,
            severity: formData.severity,
            title: formData.title.trim(),
            description: formData.description.trim(),
            status: 'open',
            reported_by: currentUserId,
          },
        ])
        .select('id, case_id')
        .single();

      if (incidentError) throw incidentError;

      if (attachment && selectedCase?.patientId) {
        const safeName = attachment.name.replace(/\s+/g, '_');
        const attachmentForUpload = new File([attachment], `incident-${incidentRow.id}-${safeName}`, {
          type: attachment.type,
        });
        await s3UploadService.uploadDocument(
          attachmentForUpload,
          selectedCase.patientId,
          selectedCaseId,
          'report',
          currentUserId
        );
      }

      toast.success('Incident cree avec succes');
      onIncidentCreated?.({ caseId: incidentRow.case_id });
      onClose?.();
    } catch (error) {
      console.error('Error creating incident from dentist hub:', error);
      toast.error(`Erreur lors de la creation de l'incident: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">Incident sur conception</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Fermer
          </Button>
        </div>

        <div className="p-6 space-y-5">
          <div className="space-y-3">
            {selectedCase && (
              <div className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2">
                <p className="text-sm font-semibold text-primary">
                  Conception selectionnee: #{selectedCase.caseNumber} - {selectedCase.patientName}
                </p>
              </div>
            )}
            <Input
              label="Rechercher une conception (numero ou patient)"
              type="text"
              placeholder="Ex: 2026-0012 ou Nom du patient"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="max-h-56 overflow-y-auto border border-border rounded-lg">
              {loadingCases ? (
                <p className="p-3 text-sm text-muted-foreground">Chargement des conceptions...</p>
              ) : filteredCases.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">Aucun resultat</p>
              ) : (
                <ul className="divide-y divide-border">
                  {filteredCases.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedCaseId(c.id)}
                        className={`w-full text-left p-3 transition-colors ${
                          selectedCaseId === c.id ? 'bg-primary/10' : 'hover:bg-muted/40'
                        }`}
                      >
                        <p className="text-sm font-medium text-foreground">Conception #{c.caseNumber}</p>
                        <p className="text-xs text-muted-foreground">Patient: {c.patientName}</p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Niveau de gravite"
              options={severityOptions}
              value={formData.severity}
              onChange={(value) => setFormData((prev) => ({ ...prev, severity: value }))}
            />
            <Select
              label="Categorie"
              options={categoryOptions}
              value={formData.category}
              onChange={(value) => setFormData((prev) => ({ ...prev, category: value }))}
            />
          </div>

          <Input
            label="Titre de l'incident"
            type="text"
            placeholder="Resume du probleme"
            value={formData.title}
            onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
            required
          />

          <div className="space-y-2">
            <label htmlFor="incident-description-detail" className="block text-sm font-medium text-foreground">
              Description détaillée
              <span className="text-destructive ml-1">*</span>
            </label>
            <Textarea
              id="incident-description-detail"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Précisez le contexte, les étapes et l'impact"
              rows={6}
              required
              className="min-h-[160px] resize-y py-3 leading-relaxed"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">Piece jointe (optionnel)</label>
            <input
              type="file"
              accept=".pdf,image/png,image/jpeg"
              onChange={handleAttachmentChange}
              disabled={submitting}
              className="block w-full text-sm text-foreground file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-muted file:text-foreground hover:file:bg-muted/80"
            />
            <p className="text-xs text-muted-foreground">Formats: PDF, PNG, JPEG - Taille max: 15 Mo</p>
            {attachment && (
              <p className="text-xs text-foreground">
                {attachment.name} ({(attachment.size / 1024 / 1024).toFixed(2)} Mo)
              </p>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-border flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Annuler
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={
              submitting ||
              !selectedCaseId ||
              !formData.title.trim() ||
              !formData.description.trim()
            }
          >
            {submitting ? 'Envoi...' : 'Envoyer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportCaseIncidentModal;
